/**
 * Event Registry
 *
 * Maps event_type_name to functional event components.
 * Used by TimelineEventList to render appropriate component for each event.
 */

import type { ComponentType } from 'react';

import { EVENT_TYPES } from '../types';
import { AgentRanEvent } from './AgentRanEvent';
import { AssociationEvent } from './AssociationEvent';
import { AutomationRanEvent } from './AutomationRanEvent';
import { CallEvent } from './CallEvent';
import { EmailEvent } from './EmailEvent';
import { EmailOpenedEvent } from './EmailOpenedEvent';
import { ErrorEvent } from './ErrorEvent';
import { EventProcessedEvent } from './EventProcessedEvent';
import { FallbackEvent } from './FallbackEvent';
import { LinkedInMessageEvent } from './LinkedInMessageEvent';
import { ObjectCreatedEvent } from './ObjectCreatedEvent';
import { ObjectDeletedEvent } from './ObjectDeletedEvent';
import { ObjectMergedEvent } from './ObjectMergedEvent';
import { ObjectRestoredEvent } from './ObjectRestoredEvent';
import { OutboundChannelEvent } from './OutboundChannelEvent';
import { PaymentEvent } from './PaymentEvent';
import { PropertyUpdatedEvent } from './PropertyUpdatedEvent';
import { SmsMessageEvent } from './SmsMessageEvent';
import type { EventComponentProps } from './types';
import { WebFormSubmittedEvent } from './WebFormSubmittedEvent';
import { WebhookReceivedEvent } from './WebhookReceivedEvent';
import { WebPageViewedEvent } from './WebPageViewedEvent';

/**
 * Registry mapping event_type_name to React component
 */
const EVENT_COMPONENTS: Record<string, ComponentType<EventComponentProps>> = {
  // Lifecycle events
  [EVENT_TYPES.OBJECT_CREATED]: ObjectCreatedEvent,
  [EVENT_TYPES.OBJECT_DELETED]: ObjectDeletedEvent,
  [EVENT_TYPES.OBJECT_MERGED]: ObjectMergedEvent,
  [EVENT_TYPES.OBJECT_RESTORED]: ObjectRestoredEvent,

  // Property events
  [EVENT_TYPES.PROPERTY_UPDATED]: PropertyUpdatedEvent,

  // Association events
  [EVENT_TYPES.ASSOCIATION_ADDED]: AssociationEvent,
  [EVENT_TYPES.ASSOCIATION_REMOVED]: AssociationEvent, // Same component handles both

  // Email events
  [EVENT_TYPES.EMAIL_SENT]: EmailEvent,
  [EVENT_TYPES.EMAIL_RECEIVED]: EmailEvent, // Same component handles both
  [EVENT_TYPES.EMAIL_OPENED]: EmailOpenedEvent,

  // LinkedIn message events
  [EVENT_TYPES.LINKEDIN_MESSAGE_SENT]: LinkedInMessageEvent,
  [EVENT_TYPES.LINKEDIN_MESSAGE_RECEIVED]: LinkedInMessageEvent,

  // Website events
  [EVENT_TYPES.WEB_FORM_SUBMITTED]: WebFormSubmittedEvent,
  [EVENT_TYPES.WEB_PAGE_VIEWED]: WebPageViewedEvent,
  [EVENT_TYPES.WEBHOOK_RECEIVED]: WebhookReceivedEvent,

  // Outbound channel events
  [EVENT_TYPES.AHOY_OUTBOUND_REQUESTED]: OutboundChannelEvent,
  [EVENT_TYPES.AHOY_OUTBOUND_DISPATCHED]: OutboundChannelEvent,
  [EVENT_TYPES.AHOY_OUTBOUND_FAILED]: OutboundChannelEvent,
  [EVENT_TYPES.AHOY_OUTBOUND_ATTEMPT_FAILED]: OutboundChannelEvent,
  [EVENT_TYPES.AHOY_OUTBOUND_ATTEMPT_CLAIMED]: OutboundChannelEvent,
  [EVENT_TYPES.AHOY_OUTBOUND_REPLAY_REQUESTED]: OutboundChannelEvent,

  // Telephony events
  [EVENT_TYPES.AHOY_CALL_COMPLETED]: CallEvent,
  [EVENT_TYPES.AHOY_CALL_MISSED]: CallEvent,
  [EVENT_TYPES.AHOY_VOICEMAIL_RECEIVED]: CallEvent,
  [EVENT_TYPES.AHOY_SMS_SENT]: SmsMessageEvent,
  [EVENT_TYPES.AHOY_SMS_RECEIVED]: SmsMessageEvent,

  // Payment events
  [EVENT_TYPES.INVOICE_PAID]: PaymentEvent,
  [EVENT_TYPES.INVOICE_PAYMENT_FAILED]: PaymentEvent,
  [EVENT_TYPES.INVOICE_REFUNDED]: PaymentEvent,

  // System events
  [EVENT_TYPES.ERRORED]: ErrorEvent,
  [EVENT_TYPES.EVENT_PROCESSED]: EventProcessedEvent,
  [EVENT_TYPES.AUTOMATION_RAN]: AutomationRanEvent,
  [EVENT_TYPES.AGENT_RAN]: AgentRanEvent,
};

/**
 * Get the appropriate event component for a given event type.
 *
 * Returns FallbackEvent for unknown event types.
 */
export function getEventComponent(
  eventType: string
): ComponentType<EventComponentProps> {
  return EVENT_COMPONENTS[eventType] || FallbackEvent;
}

/**
 * Check if a specific event type has a dedicated component.
 */
export function hasEventComponent(eventType: string): boolean {
  return eventType in EVENT_COMPONENTS;
}
